import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Select,
  Spinner,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  FiHome, FiUsers, FiKey, FiSettings, FiBook, FiLogOut, 
  FiShield, FiClipboard, FiActivity, FiRefreshCw, FiMail, FiServer,
  FiLayers, FiTrendingUp, FiHeart, FiMenu
} from 'react-icons/fi';

import Dashboard from './components/Dashboard';
import Contacts from './components/Contacts';
import APIKeys from './components/APIKeys';
import Configurations from './components/Configurations';
import Webhooks from './components/Webhooks';
import SyncRuns from './components/SyncRuns';
import SuperAdmin from './components/SuperAdmin';
import AuditLog from './components/AuditLog';
import ApiDocs from './components/ApiDocs';
import PostgresStatus from './components/PostgresStatus';
import TenantOverview from './components/TenantOverview';
import UsageAnalytics from './components/UsageAnalytics';
import HealthCheck from './components/HealthCheck';
import LoginForm from './components/Login';
import { getTenantId, setTenantId } from './tenant';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'orange' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);

  // Which Selwyn instance the pages show: the ones the service holds data for, from the tenant
  // overview, with the legacy rows that belong to no instance last
  const [tenants, setTenants] = useState([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const [tenantId, setTenantIdState] = useState(getTenantId());

  const chooseTenant = (id) => {
    setTenantId(id);
    setTenantIdState(id);
  };

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/tenants/', { headers: { 'Authorization': `Token ${token}` } })
      .then(res => (res.ok ? res.json() : { tenants: [] }))
      .then(data => {
        const list = (data.tenants || []).filter(t => t.tenant_id).concat((data.tenants || []).filter(t => !t.tenant_id));
        setTenants(list);
        if (!list.some(t => t.tenant_id === getTenantId()) && list.length) {
          setTenantId(list[0].tenant_id);
          setTenantIdState(list[0].tenant_id);
        }
      })
      .catch(() => {})
      .finally(() => setTenantsLoaded(true));
  }, [token]);

  const handleLogin = () => {
    setToken(localStorage.getItem('token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderSidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <Flex align="center" px={4} mb={6}>
        <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
          <img src="/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
        </Box>
        <Box ml={3}>
          <Text fontSize="lg" fontWeight="bold" color="white">Marketing Brevo</Text>
        </Box>
      </Flex>

      <Divider borderColor="whiteAlpha.300" mb={4} />

      <Box px={4} mb={2}>
        <Text fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
          Instance
        </Text>
        {tenants.length ? (
          <Select
            size="sm"
            bg="white"
            color="gray.800"
            borderRadius="md"
            aria-label="Instance"
            value={tenantId ?? ''}
            onChange={(e) => chooseTenant(e.target.value)}
          >
            {tenants.map(t => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.label}</option>
            ))}
          </Select>
        ) : (
          <Text fontSize="sm" color="whiteAlpha.800">
            {tenantsLoaded ? 'No instances have set up Brevo yet' : 'Loading…'}
          </Text>
        )}
      </Box>

      {/* Navigation - Main */}
      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
        Overview
      </Text>
      <NavButton to="/" icon={FiHome} isActive={location.pathname === '/'}>
        Dashboard
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Marketing
      </Text>
      <NavButton to="/contacts" icon={FiUsers} isActive={location.pathname === '/contacts'}>
        Contacts
      </NavButton>
      <NavButton to="/sync-runs" icon={FiRefreshCw} isActive={location.pathname === '/sync-runs'}>
        Sync Runs
      </NavButton>
      <NavButton to="/webhooks" icon={FiMail} isActive={location.pathname === '/webhooks'}>
        Webhooks
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Configuration
      </Text>
      <NavButton to="/api-keys" icon={FiKey} isActive={location.pathname === '/api-keys'}>
        API Keys
      </NavButton>
      <NavButton to="/configurations" icon={FiSettings} isActive={location.pathname === '/configurations'}>
        Configurations
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Administration
      </Text>
      <NavButton to="/super-admin" icon={FiShield} isActive={location.pathname === '/super-admin'}>
        Super Admin
      </NavButton>
      <NavButton to="/audit-log" icon={FiClipboard} isActive={location.pathname === '/audit-log'}>
        Audit Log
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Insights
      </Text>
      <NavButton to="/tenants" icon={FiLayers} isActive={location.pathname === '/tenants'}>
        Tenant Overview
      </NavButton>
      <NavButton to="/analytics" icon={FiTrendingUp} isActive={location.pathname === '/analytics'}>
        Usage Analytics
      </NavButton>
      <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
        Health Check
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Infrastructure
      </Text>
      <NavButton to="/postgres" icon={FiServer} isActive={location.pathname === '/postgres'}>
        PostgreSQL Status
      </NavButton>

      <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
        Developer
      </Text>
      <NavButton to="/api" icon={FiBook} isActive={location.pathname === '/api'}>
        API Documentation
      </NavButton>

      <Spacer />

      {/* User Section */}
      <Divider borderColor="whiteAlpha.300" />
      <Flex align="center" px={4} py={3}>
        <Avatar size="sm" bg="orange.400" />
        <Box ml={3} flex={1}>
          <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
          <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
        </Box>
      </Flex>
      <Button
        leftIcon={<Icon as={FiLogOut} />}
        variant="ghost"
        colorScheme="whiteAlpha"
        justifyContent="flex-start"
        color="whiteAlpha.700"
        _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
        onClick={handleLogout}
      >
        Sign Out
      </Button>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="orange.600"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">Marketing Brevo</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Sign out"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <VStack
          as="nav"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          py={6}
          px={4}
          bgGradient="linear(to-b, orange.500, orange.700)"
          spacing={2}
          align="stretch"
          boxShadow="xl"
          overflowY="auto"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </VStack>

        {/* Main Content */}
        {/* Keyed on the instance, so switching reloads the page for the new one */}
        <Box key={tenantId ?? ''} flex={1} overflowY="auto" p={{ base: 4, md: 8 }}>
          {!tenantsLoaded ? (
            <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>
          ) : (
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/sync-runs" element={<SyncRuns />} />
            <Route path="/webhooks" element={<Webhooks />} />
            <Route path="/api-keys" element={<APIKeys />} />
            <Route path="/configurations" element={<Configurations />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/tenants" element={<TenantOverview />} />
            <Route path="/analytics" element={<UsageAnalytics />} />
            <Route path="/health" element={<HealthCheck />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/api" element={<ApiDocs />} />
          </Routes>
          )}
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, orange.500, orange.700)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <VStack spacing={2} align="stretch" py={6} px={4} minH="100%">
              {renderSidebarContent()}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;
